import { API_URL } from "env";
import useUserSettingsStore from "../store/userSettingsStore";

interface LoginResponse {
    status: boolean;
    msg: string;
    data: {
        fullname: string;
        code: string;
        email: string;
        type: number;
        info: {
            id: number;
            parent_id: number;
            user_id_at: string | null;
            first_name: string;
            last_name: string;
            fullname: string;
            code: string;
            username: string | null;
            email: string;
            avatar: string;
            phone: string;
            address: string;
            is_active: number;
            created_at: string;
            updated_at: string;
            role?: {
                id?: number;
                name?: string;
                permissions?: string[];
            } | null;
            [key: string]: any;
        };
        access_token: string;
        token_type: string;
    } | null;
}

interface SignupResponse {
    status: boolean;
    msg: string;
    data: {
        fullname: string;
        first_name: string;
        last_name: string;
        phone: string;
        code: string;
        email: string;
        access_token: string;
        token_type: string;
    } | null;
}


/**
 * Authenticates a customer by sending their credentials to the login API endpoint.
 *
 * @param email - The customer's email address.
 * @param password - The customer's password.
 * @returns A promise that resolves to a {@link LoginResponse} indicating the success or failure of the authentication attempt.
 *
 * @throws Will not throw an error; instead, it catches exceptions and returns a failure response object.
 */
async function CustomerLogin(email: string, password: string): Promise<LoginResponse> {
    try {
        const data = new FormData();
        data.append('email', email);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        localStorage.setItem('token', responseData.data?.access_token || '');
        localStorage.setItem('user', JSON.stringify(responseData.data?.info || {}));
        return responseData as LoginResponse;
    } catch (error) {
        console.error('Failed to login:', error);
        return {
            status: false,
            msg: 'Login failed',
            data: null,
        };
    }
}

/**
 * Registers a new customer account by sending their personal information to the API.
 * 
 * @param firstname - The first name of the customer.
 * @param lastname - The last name of the customer.
 * @param phone - The phone number of the customer.
 * @param email - The email address for the account.
 * @param password - The account password.
 * @returns A promise that resolves to a {@link SignupResponse} containing the registration status and data.
 * 
 * @throws Will log an error to the console and return a failure state if the network request fails 
 * or the response status is not OK.
 */
async function CustomerSignup(
    firstname: string,
    lastname: string,
    phone: string,
    email: string,
    password: string
): Promise<SignupResponse> {
    try {
        const data = new FormData();
        data.append('first_name', firstname);
        data.append('last_name', lastname);
        data.append('fullname', `${firstname} ${lastname}`);
        data.append('phone', phone);
        data.append('email', email);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        return responseData as SignupResponse;
    } catch (error) {
        console.error('Failed to signup:', error);
        return {
            status: false,
            msg: 'Signup failed',
            data: null,
        };
    }
}

async function BackendLogin(email: string, password: string): Promise<LoginResponse> {
    try {
        const data = new FormData();
        data.append('email', email);
        data.append('password', password);

        const response = await fetch(`${API_URL}/api/v1/be/login`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
            },
            body: data,
        });

        const responseData = await response.json();

        if (!response.ok) {
            return {
                status: false,
                msg: responseData.msg || 'Login failed',
                data: null,
            };
        }

        localStorage.setItem('token', responseData.data?.access_token || '');
        // Giữ kèm extra_data (cài đặt cá nhân: cột bảng...) để hiển thị ngay, không chờ RefreshAuth
        localStorage.setItem('user', JSON.stringify({ ...(responseData.data?.info || {}), extra_data: responseData.data?.extra_data ?? null }));
        useUserSettingsStore.getState().hydrate(responseData.data?.extra_data);
        localStorage.setItem('userType', String(responseData.data?.type ?? ''));
        const perms = Array.isArray(responseData.data?.info?.role?.permissions)
            ? responseData.data.info.role.permissions
            : [];
        localStorage.setItem('permissions', JSON.stringify(perms));
        return responseData as LoginResponse;
    } catch (error) {
        console.error('Failed to login:', error);
        return {
            status: false,
            msg: 'Login failed',
            data: null,
        };
    }
}

/**
 * Đồng bộ lại quyền của nhân viên đang đăng nhập từ backend.
 * Dùng để chống tình trạng admin đổi nhóm quyền nhưng client vẫn dùng quyền cũ trong localStorage.
 *
 * Trả về:
 *   - ok=true + type + permissions khi đồng bộ thành công.
 *   - ok=false khi không có token/user hoặc backend trả lỗi không phải 401/403.
 *   - Khi backend trả 401/403 → tự logout và redirect /login (không trả về).
 */
async function RefreshAuth(): Promise<{
    ok: boolean;
    type?: number | null;
    permissions?: string[];
}> {
    const token = localStorage.getItem('token');
    if (!token) return { ok: false };

    try {
        const response = await fetch(`${API_URL}/api/v1/be/profile`, {
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });

        // 403 là thiếu quyền, không phải hết phiên
        if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userType');
            localStorage.removeItem('permissions');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            return { ok: false };
        }

        if (!response.ok) return { ok: false };

        const data = await response.json();
        const info = data?.data ?? data;
        if (!info || typeof info !== 'object') return { ok: false };

        const type =
            typeof info.type === 'number'
                ? info.type
                : Number(localStorage.getItem('userType') || 0) || null;
        const perms = Array.isArray(info?.role?.permissions)
            ? (info.role.permissions as string[])
            : [];

        localStorage.setItem('user', JSON.stringify(info));
        useUserSettingsStore.getState().hydrate(info.extra_data);
        localStorage.setItem('userType', type === null ? '' : String(type));
        localStorage.setItem('permissions', JSON.stringify(perms));

        return { ok: true, type, permissions: perms };
    } catch (error) {
        console.error('RefreshAuth failed:', error);
        return { ok: false };
    }
}

export {
    CustomerLogin,
    CustomerSignup,
    BackendLogin,
    RefreshAuth,
}